import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Globe, Plus, Pencil, Trash2, ExternalLink, Search, Wand2, ArrowRight } from "lucide-react";

export default function SitePages() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [templates, setTemplates] = useState([]);

  const load = () => api.get("/site/pages").then((r) => setPages(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/site/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const createFromTemplate = async (key) => {
    const { data } = await api.post(`/site/pages/from-template/${key}`);
    toast.success("Page créée depuis le modèle");
    navigate(`/pages/${data.id}`);
  };

  const publicUrl = (slug) => `${window.location.origin}/site/${user?.company_id}/page/${slug}`;

  const remove = async (id) => {
    await api.delete(`/site/pages/${id}`);
    toast.success("Page supprimée");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center">
            <Globe className="w-5 h-5 text-sky-400" />
          </div>
          <div>
            <h1 className="text-3xl font-display font-bold text-navy">Site & Pages de présentation</h1>
            <p className="text-slate-500">Créez des pages vitrines optimisées SEO & référencement local (GEO)</p>
          </div>
        </div>
        <button onClick={() => navigate("/pages/new")} data-testid="new-page-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-400 text-navy font-semibold hover:bg-sky-500 transition-colors">
          <Plus className="w-4 h-4" /> Nouvelle page
        </button>
      </div>

      {templates.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Wand2 className="w-5 h-5 text-sky-500" />
            <h3 className="font-display font-semibold text-navy">Créer depuis un modèle</h3>
            <span className="text-xs text-slate-400">personnalisable en un clic</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {templates.map((t) => (
              <button key={t.key} onClick={() => createFromTemplate(t.key)} data-testid={`template-${t.key}`}
                className="text-left p-4 rounded-xl border border-slate-200 hover:border-sky-400 hover:bg-sky-50/40 transition-colors">
                <div className="text-sm font-semibold text-navy">{t.name}</div>
                <div className="text-xs text-slate-500 mt-1">{t.description}</div>
                <span className="mt-2 inline-flex items-center gap-1 text-xs text-sky-600 font-semibold">Utiliser ce modèle <ArrowRight className="w-3 h-3" /></span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {pages.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col" data-testid={`page-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center"><Globe className="w-5 h-5 text-sky-600" /></div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${p.published ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {p.published ? "En ligne" : "Brouillon"}
              </span>
            </div>
            <h3 className="font-display font-semibold text-navy mt-3 text-lg leading-tight">{p.title}</h3>
            <p className="text-xs text-slate-500 font-mono mt-1 truncate">/site/.../page/{p.slug}</p>
            {p.seo?.keywords?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {p.seo.keywords.slice(0, 3).map((k, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px]"><Search className="w-2.5 h-2.5" />{k}</span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1 mt-4 pt-4 border-t border-slate-100">
              <a href={publicUrl(p.slug)} target="_blank" rel="noreferrer" data-testid={`view-online-${p.id}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep transition-colors">
                <ExternalLink className="w-4 h-4" /> Voir en ligne
              </a>
              <button onClick={() => navigate(`/pages/${p.id}`)} data-testid={`edit-page-${p.id}`} className="p-2 rounded-xl border border-slate-200 text-slate-600 hover:border-sky-300"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(p.id)} data-testid={`delete-page-${p.id}`} className="p-2 rounded-xl border border-slate-200 text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {pages.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-400">
            <Globe className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Aucune page. Créez votre première page vitrine.
          </div>
        )}
      </div>
    </div>
  );
}
